/**
 * P4-4 — jobsite photo capture.
 *
 * The rule this screen is built around: **pressing the button always
 * succeeds.** A photo is written to the local queue first and uploaded second,
 * even when the device is online and the connection is good. The crew is never
 * shown a failed upload they have to understand, because on a jobsite the
 * response to a failed upload is to shrug and walk away, and the photo — which
 * is the evidence a reimbursement is later argued from — is gone.
 *
 * That also means there is no separate offline code path to rot. Online and
 * offline differ only in how long an item sits in the queue.
 *
 * What this screen refuses to do is claim more than it knows. A photo whose
 * camera never wrote a UTC offset gets "time not verifiable", not a guessed
 * timestamp. A photo with no coordinates is "location not recorded", not
 * "off site". The difference matters when the document is read back two years
 * later by somebody adjusting a claim.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  type PhotoCapture as CaptureMeta,
  type PillarCode,
  type Project,
  type UploadResult,
} from '../lib/api';
import {
  drainQueue,
  enqueue,
  listQueued,
  queueSupported,
  removeQueued,
  retryQueued,
  MAX_AUTOMATIC_ATTEMPTS,
  type QueuedPhoto,
} from '../lib/photo-queue';

interface Props {
  scope: PillarCode | null;
}

/** An accepted upload, kept on screen so the crew sees what was recorded. */
interface Accepted {
  id: string;
  title: string;
  capture: CaptureMeta;
}

export function PhotoCapture({ scope }: Props): JSX.Element {
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState('');
  const [note, setNote] = useState('');
  const [queue, setQueue] = useState<QueuedPhoto[]>([]);
  const [accepted, setAccepted] = useState<Accepted[]>([]);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const draining = useRef(false);

  const supported = queueSupported();

  useEffect(() => {
    let live = true;
    api
      .projects(scope)
      .then((res) => {
        if (!live) return;
        setProjects(res.projects);
        setProjectId((current) => current || (res.projects[0]?.id ?? ''));
      })
      .catch(() => setError('Projects could not be loaded, so there is nothing to file against.'));
    return () => {
      live = false;
    };
  }, [scope]);

  const refreshQueue = useCallback(async () => {
    if (!supported) return;
    setQueue(await listQueued());
  }, [supported]);

  /**
   * Drains the queue, guarded against overlap: the reconnect event and the
   * post-capture drain can fire within the same tick, and two drains over one
   * store would upload the same photo twice.
   */
  const drain = useCallback(async () => {
    if (!supported || draining.current || !navigator.onLine) return;
    draining.current = true;
    setBusy(true);
    try {
      await drainQueue(async (item) => {
        const result: UploadResult = await api.uploadDocument({
          file: item.file,
          filename: item.filename,
          projectId: item.projectId,
          category: item.category,
          title: item.title,
          description: item.description,
          tags: item.tags,
        });
        setAccepted((prev) =>
          [{ id: result.id, title: result.title, capture: result.capture }, ...prev].slice(0, 12),
        );
      });
    } finally {
      draining.current = false;
      setBusy(false);
      await refreshQueue();
    }
  }, [supported, refreshQueue]);

  useEffect(() => {
    void refreshQueue().then(drain);
  }, [refreshQueue, drain]);

  // Coming back into coverage is the moment the queue exists for.
  useEffect(() => {
    const goOnline = (): void => {
      setOnline(true);
      void drain();
    };
    const goOffline = (): void => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [drain]);

  async function onFiles(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = Array.from(event.target.files ?? []);
    event.target.value = ''; // so the same photo can be re-selected
    if (files.length === 0) return;
    if (!projectId) {
      setError('Choose a project before adding photos.');
      return;
    }
    setError(null);

    for (const file of files) {
      await enqueue({
        file,
        filename: file.name || `photo-${Date.now()}.jpg`,
        projectId,
        category: 'photo',
        description: note.trim() || undefined,
      });
    }
    setNote('');
    await refreshQueue();
    void drain();
  }

  const project = projects.find((p) => p.id === projectId);
  const stuck = queue.filter((q) => q.attempts >= MAX_AUTOMATIC_ATTEMPTS);
  const waiting = queue.filter((q) => q.attempts < MAX_AUTOMATIC_ATTEMPTS);

  return (
    <section className="flex flex-col gap-4">
      <header>
        <h1 className="text-h1 font-semibold text-text-primary">Site photos</h1>
        <p className="mt-1 max-w-prose text-body text-text-secondary">
          Photos are held on this device first and uploaded when there is a connection, so a photo
          taken with no signal is never lost. Capture time and location are read from the photo
          itself &mdash; nothing is entered by hand and nothing is guessed.
        </p>
      </header>

      {!supported ? (
        <div className="card border-warning p-4">
          <p className="text-body text-text-primary">
            This browser cannot store a queue, so photos would be lost if an upload failed. Use the
            device camera app and upload from the document library instead.
          </p>
        </div>
      ) : null}

      {!online ? (
        <div className="card p-4" style={{ borderColor: 'hsl(var(--warning))' }}>
          <p className="text-body font-medium text-text-primary">Offline &mdash; still capturing</p>
          <p className="mt-1 text-body text-text-secondary">
            Photos are being saved on this device. They upload on their own as soon as the
            connection comes back; this page does not need to stay open for them to be kept.
          </p>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="text-body text-danger">
          {error}
        </p>
      ) : null}

      {/* ------------------------------------------------------- capture */}
      <div className="card p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:gap-4">
          <label className="flex flex-col gap-1 md:w-80">
            <span className="micro-label text-text-secondary">Project</span>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="min-h-touch rounded-card border border-border bg-white px-3 py-1.5 text-body text-text-primary"
            >
              {projects.length === 0 ? <option value="">No projects in scope</option> : null}
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} &middot; {p.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-1 flex-col gap-1">
            <span className="micro-label text-text-secondary">Note (optional)</span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What the photo shows"
              className="min-h-touch rounded-card border border-border bg-white px-3 py-1.5 text-body text-text-primary"
            />
          </label>
        </div>

        {/*
          `capture="environment"` asks a phone for the rear camera directly.
          Desktop browsers ignore the attribute and show a file picker, so one
          control serves both without a device check.
        */}
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={onFiles}
          className="sr-only"
        />
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={!supported || !projectId}
            className="btn-primary min-h-touch rounded-card px-5 text-body font-semibold disabled:opacity-40"
          >
            Add photos
          </button>
          {project ? (
            <span className="text-micro text-text-secondary">
              Filed to {project.code} &middot; {project.name}
            </span>
          ) : null}
          {busy ? <span className="text-micro text-text-secondary">Uploading&hellip;</span> : null}
        </div>
      </div>

      {/* --------------------------------------------------------- queue */}
      {queue.length > 0 ? (
        <div className="card p-4">
          <h2 className="text-h2 font-semibold text-text-primary">
            On this device{' '}
            <span className="numeric text-text-secondary">({queue.length})</span>
          </h2>
          <p className="mt-1 text-body text-text-secondary">
            {waiting.length > 0
              ? 'Waiting to upload. These are safe on this device until they do.'
              : 'Every remaining photo needs attention before it can upload.'}
          </p>
          <ul className="mt-3 flex flex-col gap-2">
            {queue.map((item) => {
              const failed = item.attempts >= MAX_AUTOMATIC_ATTEMPTS;
              return (
                <li
                  key={item.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-border px-3 py-2"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-body text-text-primary">
                      {item.filename}
                    </span>
                    <span className="text-micro text-text-secondary">
                      {failed
                        ? `Not accepted \u2014 ${item.lastError ?? 'upload refused'}`
                        : `Queued ${new Date(item.queuedAt).toLocaleTimeString()}`}
                      {item.attempts > 0 && !failed ? ` \u00b7 ${item.attempts} attempts` : ''}
                    </span>
                  </span>
                  {failed ? (
                    <span className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => void retryQueued(item.id).then(refreshQueue).then(drain)}
                        className="btn-secondary min-h-touch rounded-card px-3 text-body"
                      >
                        Retry
                      </button>
                      <button
                        type="button"
                        onClick={() => void removeQueued(item.id).then(refreshQueue)}
                        className="btn-destructive min-h-touch rounded-card px-3 text-body"
                      >
                        Discard
                      </button>
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {stuck.length > 0 ? (
            <p className="mt-3 text-body text-text-secondary">
              Nothing is deleted automatically, however many times it fails. A photo stays here
              until it uploads or somebody discards it deliberately.
            </p>
          ) : null}
        </div>
      ) : null}

      {/* ------------------------------------------------------ accepted */}
      {accepted.length > 0 ? (
        <div className="card p-4">
          <h2 className="text-h2 font-semibold text-text-primary">Uploaded this session</h2>
          <ul className="mt-3 flex flex-col gap-2">
            {accepted.map((item) => (
              <li key={item.id} className="rounded-card border border-border px-3 py-2">
                <p className="truncate text-body text-text-primary">{item.title}</p>
                <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-micro text-text-secondary">
                  <span>{captureTimeText(item.capture)}</span>
                  <span>{siteText(item.capture)}</span>
                </p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {queue.length === 0 && accepted.length === 0 ? (
        <div className="empty-state">
          <p className="text-body font-medium text-text-primary">No photos captured yet</p>
          <p className="mt-1 text-body">
            Photos added here are written into the project&rsquo;s Dropbox folder and listed in the
            document library.
          </p>
        </div>
      ) : null}
    </section>
  );
}

/**
 * The capture time in the words the reader needs. A photo whose zone was never
 * recorded says so plainly rather than showing a time that looks authoritative
 * and is off by up to a day.
 */
export function captureTimeText(capture: CaptureMeta): string {
  if (capture.capturedAt) {
    const when = new Date(capture.capturedAt).toLocaleString();
    return capture.capturedAtBasis === 'gps_utc'
      ? `Taken ${when} (satellite clock)`
      : `Taken ${when}`;
  }
  if (capture.capturedAtLocalText) {
    return `Camera read ${capture.capturedAtLocalText} \u2014 time zone not recorded, so the exact time is not verifiable`;
  }
  if (capture.exifSkipReason === 'no_exif_present') {
    return 'No capture time in the photo';
  }
  return 'Capture time not recorded';
}

/** Location, with the two kinds of "unverified" kept apart. */
export function siteText(capture: CaptureMeta): string {
  switch (capture.siteBasis) {
    case 'within_radius':
      return capture.siteDistanceMeters === null
        ? 'On site'
        : `On site (${Math.round(capture.siteDistanceMeters)} m from the site point)`;
    case 'outside_radius':
      return capture.siteDistanceMeters === null
        ? 'Not on site'
        : `${formatDistance(capture.siteDistanceMeters)} from the site point`;
    case 'no_photo_gps':
      return 'Location not recorded by the camera';
    case 'no_site_coordinates':
      return 'Project has no site point to check against';
    default:
      return 'Location not checked';
  }
}

function formatDistance(meters: number): string {
  return meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)} m`;
}
